import { env, AppEnvironment } from "@core/config/env";
import { getStoredToken, handleJwtExpiration } from "@core/utils/auth";
import * as Sentry from "@sentry/react";

export type ApiFetchOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  headers?: Record<string, string>;
  body?: unknown;
  noAuth?: boolean; // set true to skip Authorization header
};

// Unified fetch wrapper with base URL, auth, JSON handling and error normalization
export async function apiFetch<T = unknown>(
  url: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const { method = "GET", headers = {}, body, noAuth } = options;

  // Support passing relative paths; prepend base URL
  const finalUrl = /^(http|https):\/\//i.test(url)
    ? url
    : `${env.apiBaseUrl}${url.startsWith("/") ? "" : "/"}${url}`;

  const token = !noAuth ? getStoredToken() : null;

  const isFormData =
    typeof FormData !== "undefined" && body instanceof FormData;

  const mergedHeaders: Record<string, string> = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...headers,
  };

  const response = await fetch(finalUrl, {
    method,
    headers: mergedHeaders,
    body:
      body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
  });

  const contentType = response.headers.get("content-type") || "";
  const parseJson = contentType.includes("application/json");

  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    try {
      const raw = await response.text();
      if (raw) {
        if (parseJson) {
          const data = JSON.parse(raw);
          message = (data as { message?: string })?.message || message;
          // Optionally attach details
          throw new Error(message);
        } else {
          message = raw;
          throw new Error(message);
        }
      }
    } catch (e: unknown) {
      // If JSON.parse throws or any other error, rethrow with best-effort message
      const errMsg = e instanceof Error ? e.message : message;

      // Handle JWT expiration
      if (errMsg.toLowerCase().includes("jwt expired")) {
        return handleJwtExpiration();
      }

      if (env.environment === AppEnvironment.Production) {
        Sentry.captureException(new Error(errMsg), {
          extra: {
            url: finalUrl,
            method,
            status: response.status,
          },
        });
      }

      throw new Error(errMsg);
    }

    // Handle JWT expiration in the main error message as well
    if (message.toLowerCase().includes("jwt expired")) {
      return handleJwtExpiration();
    }

    if (env.environment === AppEnvironment.Production) {
      Sentry.captureException(new Error(message), {
        extra: {
          url: finalUrl,
          method,
          status: response.status,
        },
      });
    }

    throw new Error(message);
  }

  if (!contentType) return undefined as unknown as T;
  if (parseJson) return (await response.json()) as T;
  // For non-JSON responses, return text as-is
  return (await response.text()) as unknown as T;
}
